import { assertNever } from "@@/shared/assert-never";
import { type OrderGroupId } from "@@/shared/order-groups/order-group-id";
import type { OrderEventId } from "@@/shared/orders/order-events/events/_order-event-id";
import type { OrderChangeRefundRequestedEvent } from "@@/shared/orders/order-events/events/order-change-refund-requested.event";
import type { OrderFullRefundRequestedEvent } from "@@/shared/orders/order-events/events/order-full-refund-requested.event";
import type { OrderPartialRefundRequestedEvent } from "@@/shared/orders/order-events/events/order-partial-refund-requested.event";
import { payRequestItemFactory } from "@@/shared/pay-requests/pay-request-item";
import type { Price} from "@@/shared/prices/price";
import { zeroPrice } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { type ProviderId } from "@@/shared/providers/provider-id";
import { translation } from "@@/shared/translations/translation";
import { asArray } from "@@/utilities/as-array";
import { exists } from "@@/utilities/exists";
import { sortBy } from "@@/utilities/sort";
import { unique } from "@@/utilities/unique";
import { type OrderEvent } from "../../order-events/events/_order-event";
import type {
    OrderChangedEvent} from "../../order-events/events/order-changed-event/order-changed.event";
import {
    isOrderChangedEvent,
} from "../../order-events/events/order-changed-event/order-changed.event";
import type {
    OrderCreatedEvent} from "../../order-events/events/order-created.event";
import {
    isOrderCreatedEvent,
} from "../../order-events/events/order-created.event";
import { type OrderPayRequestEvent } from "../../order-events/events/order-pay-request-events/_order-pay-request.event";
import type { OrderPaymentAmountReleasedEvent } from "../../order-events/events/order-payment-events/order-payment-amount-released.event";
import type { OrderPaymentFailedEvent } from "../../order-events/events/order-payment-events/order-payment-failed.event";
import type { OrderPaymentFulfilledEvent } from "../../order-events/events/order-payment-events/order-payment-fulfilled.event";
import type { OrderPayLaterOnPremiseCancelledEvent } from "../../order-events/events/order-payment-events/order-payment-pay-later-on-premise-cancelled.event";
import type { OrderPaymentPendingEvent } from "../../order-events/events/order-payment-events/order-payment-pending.event";
import type { OrderPendingPaymentConfirmationEvent } from "../../order-events/events/order-payment-events/order-pending-payment-confirmation.event";
import { type OrderId } from "../../order-id";
import { type LegacyFees } from "../financial/legacy-fees";
import { financialOrderInformationForCustomerEventIdFactory } from "./financial-order-information-for-customer-event-id";
import { financialOrderInformationForCustomerProjectionIdFactory } from "./financial-order-information-for-customer-id";
import { type FinancialOrderInformationForCustomerProjection } from "./financial-order-information-for-customer.projection";
import {
    isRefundReceiptForCustomer,
    type ReceiptForCustomer,
    type RefundReceiptForCustomer,
    type RefundTransactionStatus,
} from "./receipt-for-customer";

const receiptSignatureForRefundFactory = (
    refundRequestEvent:
        | OrderFullRefundRequestedEvent
        | OrderPartialRefundRequestedEvent
        | OrderChangeRefundRequestedEvent,
): `REFUND|${OrderEventId}` => {
    return `REFUND|${refundRequestEvent._id}`;
};

const _payRequestFinancialOrderHistoryEventFactory = (params: {
    status: "PENDING" | "CANCELLED" | "FULFILLED";
    parents: (OrderCreatedEvent | OrderChangedEvent)[];
    orderEvent: OrderPayRequestEvent;
    orderId: OrderId;
    orderGroupId: OrderGroupId;
}): ReceiptForCustomer => {
    const parents = params.parents;
    const { orderGroupId, orderId, orderEvent } = params;

    const historyEvent: ReceiptForCustomer = {
        _id: financialOrderInformationForCustomerEventIdFactory(),
        _type: "FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT",
        _createdAt: orderEvent._createdAt,
        type: "PAY_REQUEST_PAYMENT",
        status: params.status,
        originTypes: unique(
            parents
                .map(parent => {
                    switch (parent.type) {
                        case "CREATED":
                            return "ORDER_CREATED";
                        case "ORDER_CHANGED":
                            return "ORDER_CHANGED";
                        default:
                            return undefined;
                    }
                })
                .filter(exists),
        ),
        receiptFor: {
            orderGroupId,
            orderId,
            providerId: orderEvent.providerId,
            orderEventIds: orderEvent.paymentFor.orderEventIds,
        },
        items: parents.flatMap(parent => {
            switch (parent.type) {
                case "CREATED":
                    return payRequestItemFactory.fromOrderCreated({
                        orderCreatedEvent: parent,
                    });
                case "ORDER_CHANGED":
                    return parent.changes.map(change =>
                        payRequestItemFactory.fromOrderChange(
                            change,
                            parent._id,
                            parent._createdAt,
                        ),
                    );
                default:
                    assertNever(parent, "Unknown order event type");
            }
        }),
        signature: `PAY_REQUEST|${parents
            .map(parent => parent._id)
            .join("|")}|${orderEvent.payRequestId}`,
        payRequestId: orderEvent.payRequestId,
        amount: sumPrices(
            parents.map(parent => {
                switch (parent.type) {
                    case "CREATED":
                        return parent.order.totalPrice;
                    case "ORDER_CHANGED":
                        return sumPrices(
                            parent.changes.map(change => change.price),
                        );
                    default:
                        assertNever(parent, "Unknown order event type");
                }
            }),
        ),
    };
    return historyEvent;
};

const _onPremiseFinancialOrderHistoryEventFactory = (params: {
    status: "PENDING" | "CANCELLED" | "FULFILLED";
    parents: (OrderCreatedEvent | OrderChangedEvent)[];
    orderEvent:
        | OrderPaymentPendingEvent
        | OrderPayLaterOnPremiseCancelledEvent
        | OrderPaymentFulfilledEvent;
    orderId: OrderId;
    orderGroupId: OrderGroupId;
}): ReceiptForCustomer => {
    const parents = params.parents;
    const { orderGroupId, orderId, orderEvent } = params;

    const historyEvent: ReceiptForCustomer = {
        _id: financialOrderInformationForCustomerEventIdFactory(),
        _type: "FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT",
        _createdAt: orderEvent._createdAt,
        type: "ON_PREMISE_PAYMENT",
        status: params.status,
        originTypes: unique(
            parents
                .map(parent => {
                    switch (parent.type) {
                        case "CREATED":
                            return "ORDER_CREATED";
                        case "ORDER_CHANGED":
                            return "ORDER_CHANGED";
                        default:
                            return undefined;
                    }
                })
                .filter(exists),
        ),
        receiptFor: {
            orderGroupId,
            orderId,
            providerId: orderEvent.providerId,
            orderEventIds: asArray(orderEvent.paymentFor.orderEventId),
        },
        items: parents.flatMap(parent => {
            switch (parent.type) {
                case "CREATED":
                    return payRequestItemFactory.fromOrderCreated({
                        orderCreatedEvent: parent,
                    });
                case "ORDER_CHANGED":
                    return parent.changes.map(change =>
                        payRequestItemFactory.fromOrderChange(
                            change,
                            parent._id,
                            parent._createdAt,
                        ),
                    );
                default:
                    assertNever(parent, "Unknown order event type");
            }
        }),
        signature: `ON_PREMISE|${parents.map(parent => parent._id).join("|")}`,
        amount: sumPrices(
            parents.map(parent => {
                switch (parent.type) {
                    case "CREATED":
                        return parent.order.totalPrice;
                    case "ORDER_CHANGED":
                        return sumPrices(
                            parent.changes.map(change => change.price),
                        );
                    default:
                        assertNever(parent, "Unknown order event type");
                }
            }),
        ),
    };
    return historyEvent;
};

const _waivedFinancialOrderHistoryEventFactory = (params: {
    status: "PENDING" | "CANCELLED" | "FULFILLED";
    parents: (OrderCreatedEvent | OrderChangedEvent)[];
    orderEvent:
        | OrderPaymentPendingEvent
        | OrderPayLaterOnPremiseCancelledEvent
        | OrderPaymentFulfilledEvent;
    orderId: OrderId;
    orderGroupId: OrderGroupId;
}): ReceiptForCustomer => {
    const parents = params.parents;
    const { orderGroupId, orderId, orderEvent } = params;

    const historyEvent: ReceiptForCustomer = {
        _id: financialOrderInformationForCustomerEventIdFactory(),
        _type: "FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT",
        _createdAt: orderEvent._createdAt,
        type: "WAIVED_PAYMENT",
        status: params.status,
        originTypes: unique(
            parents
                .map(parent => {
                    switch (parent.type) {
                        case "CREATED":
                            return "ORDER_CREATED";
                        case "ORDER_CHANGED":
                            return "ORDER_CHANGED";
                        default:
                            return undefined;
                    }
                })
                .filter(exists),
        ),
        receiptFor: {
            orderGroupId,
            orderId,
            providerId: orderEvent.providerId,
            orderEventIds: asArray(orderEvent.paymentFor.orderEventId),
        },
        items: parents.flatMap(parent => {
            switch (parent.type) {
                case "CREATED":
                    return payRequestItemFactory.fromOrderCreated({
                        orderCreatedEvent: parent,
                    });
                case "ORDER_CHANGED":
                    return parent.changes.map(change =>
                        payRequestItemFactory.fromOrderChange(
                            change,
                            parent._id,
                            parent._createdAt,
                        ),
                    );
                default:
                    assertNever(parent, "Unknown order event type");
            }
        }),
        signature: `WAIVED|${parents.map(parent => parent._id).join("|")}`,
        amount: sumPrices(
            parents.map(parent => {
                switch (parent.type) {
                    case "CREATED":
                        return parent.order.totalPrice;
                    case "ORDER_CHANGED":
                        return sumPrices(
                            parent.changes.map(change => change.price),
                        );
                    default:
                        assertNever(parent, "Unknown order event type");
                }
            }),
        ),
    };
    return historyEvent;
};

const _paymentFinancialOrderHistoryEventFactory = (params: {
    status: "PENDING" | "CANCELLED" | "FULFILLED";
    /** if order event is the created event this should be set to undefined */
    parent: OrderCreatedEvent | undefined;
    orderEvent:
        | OrderCreatedEvent
        | OrderPendingPaymentConfirmationEvent
        | OrderPaymentFailedEvent
        | OrderPaymentFulfilledEvent
        | OrderPaymentAmountReleasedEvent;
    orderId: OrderId;
    orderGroupId: OrderGroupId;
    legacyFees?: LegacyFees;
}): ReceiptForCustomer => {
    const { parent, orderGroupId, orderId, orderEvent } = params;
    const orderCreatedEvent =
        orderEvent.type === "CREATED" ? orderEvent : parent;
    if (!orderCreatedEvent) throw new Error("Order created event required");
    const historyEvent: ReceiptForCustomer = {
        _id: financialOrderInformationForCustomerEventIdFactory(),
        _type: "FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT",
        _createdAt: orderEvent._createdAt,
        type: "PAYMENT",
        status: params.status,
        originTypes: ["ORDER_CREATED"],
        receiptFor: {
            orderGroupId,
            orderId,
            providerId: orderEvent.providerId,
            orderEventIds:
                parent && parent.type !== "CREATED" ? [parent._id] : [],
        },
        items: [
            ...payRequestItemFactory.fromOrderCreated({
                orderCreatedEvent,
            }),
            ...(params.legacyFees
                ? [
                      ...(params.legacyFees.shipmentFee
                          ? [
                                payRequestItemFactory.from({
                                    name: translation({
                                        sv: "Fraktavgift",
                                        en: "Shipment fee",
                                    }),
                                    pricePerItem: params.legacyFees.shipmentFee,
                                    quantity: 1,
                                    providerId:
                                        orderCreatedEvent.order.providerId,
                                    type: "ADDED",
                                    references: {
                                        orderEventId: orderCreatedEvent._id,
                                        orderGroupId,
                                        orderId,
                                        providerId:
                                            orderCreatedEvent.order.providerId,
                                    },
                                }),
                            ]
                          : []),
                      ...(params.legacyFees.extraOrderFee
                          ? [
                                payRequestItemFactory.from({
                                    name: translation({
                                        sv: "Tillägg för för liten beställning",
                                        en: "Extra order fee",
                                    }),
                                    pricePerItem:
                                        params.legacyFees.extraOrderFee,
                                    quantity: 1,
                                    providerId:
                                        orderCreatedEvent.order.providerId,
                                    type: "ADDED",
                                    references: {
                                        orderEventId: orderCreatedEvent._id,
                                        orderGroupId,
                                        orderId,
                                        providerId:
                                            orderCreatedEvent.order.providerId,
                                    },
                                }),
                            ]
                          : []),
                      ...(params.legacyFees.serviceFee
                          ? [
                                payRequestItemFactory.from({
                                    name: translation({
                                        sv: "Serviceavgift",
                                        en: "Service fee",
                                    }),
                                    pricePerItem: params.legacyFees.serviceFee,
                                    quantity: 1,
                                    providerId:
                                        orderCreatedEvent.order.providerId,
                                    type: "ADDED",
                                    references: {
                                        orderEventId: orderCreatedEvent._id,
                                        orderGroupId,
                                        orderId,
                                        providerId:
                                            orderCreatedEvent.order.providerId,
                                    },
                                }),
                            ]
                          : []),
                  ]
                : []),
        ],
        signature: `PAYMENT|${orderCreatedEvent._id}`,
        amount: orderCreatedEvent.order.totalPrice,
    };
    return historyEvent;
};

const getFinancialOrderInformationForCustomer = (params: {
    orderGroupId: OrderGroupId;
    orderId: OrderId;
    providerId: ProviderId;
    orderEvents: OrderEvent[];
    legacyServiceFee?: Price;
}): FinancialOrderInformationForCustomerProjection => {
    const { orderGroupId, providerId, orderId, orderEvents } = params;
    const orderCreatedEvent = orderEvents.find(
        item => item.type === "CREATED",
    ) as OrderCreatedEvent | undefined;
    if (!orderCreatedEvent) throw new Error("Order created event required");

    const initialTotalAmount = sumPrices([
        orderCreatedEvent.order.totalPrice,
        params.legacyServiceFee ?? zeroPrice,
    ]);

    const initial: FinancialOrderInformationForCustomerProjection = {
        _id: financialOrderInformationForCustomerProjectionIdFactory(),
        _type: "ORDER_HISTORY_FOR_CUSTOMER_PROJECTION",
        _createdAt: orderCreatedEvent._createdAt,
        status: "PENDING",
        current: {
            amountCapturable: 0,
            amountRefunded: 0,
            amountPendingRefund: 0,
            amountSettled: 0,
            amountUnsettled: initialTotalAmount.amountIncludingVat,
        },
        for: {
            orderGroupId,
            orderId,
            providerId,
        },
        receipts: [],
    };

    const financialOrderInformationForCustomerReducer = (
        state: FinancialOrderInformationForCustomerProjection,
        orderEvent: OrderEvent,
    ): FinancialOrderInformationForCustomerProjection => {
        const getFinancialOrderInformationForCustomerEvent = ():
            | ReceiptForCustomer
            | undefined => {
            switch (orderEvent.type) {
                case "CREATED": {
                    return _paymentFinancialOrderHistoryEventFactory({
                        status: "PENDING",
                        parent: undefined,
                        orderEvent: orderCreatedEvent,
                        orderId,
                        orderGroupId,
                        legacyFees: {
                            extraOrderFee:
                                orderCreatedEvent.order.extraOrderFee ??
                                zeroPrice,
                            serviceFee: params.legacyServiceFee ?? zeroPrice,
                            shipmentFee:
                                orderCreatedEvent.order.delivery
                                    .deliveryMethodType === "HOME_DELIVERY"
                                    ? orderCreatedEvent.order.delivery.price
                                    : zeroPrice,
                        },
                    });
                }
                // Pay request events
                case "PAY_REQUEST_PENDING":
                case "PAY_REQUEST_CANCELLED":
                case "PAY_REQUEST_FULFILLED":
                case "PAY_REQUEST_EXPIRED": {
                    const parents = orderEvent.paymentFor.orderEventIds
                        .map(orderEventId => {
                            const orderEvent = orderEvents.find(
                                event => event._id === orderEventId,
                            );
                            if (!orderEvent)
                                throw new Error(
                                    `Order event ${orderEventId} not found`,
                                );
                            if (
                                !isOrderCreatedEvent(orderEvent) &&
                                !isOrderChangedEvent(orderEvent)
                            )
                                return undefined;
                            return orderEvent;
                        })
                        .filter(exists) as (
                        | OrderCreatedEvent
                        | OrderChangedEvent
                    )[];

                    const getStatus = () => {
                        switch (orderEvent.type) {
                            case "PAY_REQUEST_PENDING":
                                return "PENDING";
                            case "PAY_REQUEST_CANCELLED":
                            case "PAY_REQUEST_EXPIRED":
                                return "CANCELLED";
                            case "PAY_REQUEST_FULFILLED":
                                return "FULFILLED";
                            default:
                                assertNever(
                                    orderEvent,
                                    "Unknown order event type",
                                );
                        }
                    };

                    return _payRequestFinancialOrderHistoryEventFactory({
                        status: getStatus(),
                        parents,
                        orderEvent,
                        orderId,
                        orderGroupId,
                    });
                }

                // On premise payment events
                // ON_PREMISE_PAY_LATER_CANCELLED
                // ON_PREMISE_PAY_LATER_PENDING
                case "ON_PREMISE_PAY_LATER_CANCELLED":
                case "PENDING_PAYMENT_ONPREMISE": {
                    const parents = asArray(orderEvent.paymentFor.orderEventId)
                        .map(orderEventId => {
                            const orderEvent = orderEvents.find(
                                event => event._id === orderEventId,
                            );
                            if (!orderEvent)
                                throw new Error(
                                    `Order event ${orderEventId} not found`,
                                );
                            if (
                                !isOrderCreatedEvent(orderEvent) &&
                                !isOrderChangedEvent(orderEvent)
                            )
                                return undefined;
                            return orderEvent;
                        })
                        .filter(exists) as (
                        | OrderCreatedEvent
                        | OrderChangedEvent
                    )[];

                    return _onPremiseFinancialOrderHistoryEventFactory({
                        status:
                            orderEvent.type === "PENDING_PAYMENT_ONPREMISE"
                                ? "PENDING"
                                : "CANCELLED",
                        parents,
                        orderEvent,
                        orderId,
                        orderGroupId,
                    });
                }

                // Payment fulfilled
                // ON PREMISE OR
                // DEFAULT PAYMENT
                case "PAYMENT_FULFILLED": {
                    const onPremise =
                        orderEvent.paymentProvider === "ON_PREMISE";
                    const waived = orderEvent.paymentProvider === "FREE";
                    const orderCreatedEvent = orderEvents.find(
                        item => item.type === "CREATED",
                    ) as OrderCreatedEvent | undefined;
                    if (!orderCreatedEvent)
                        throw new Error("Order created event not found");

                    const parents = asArray(orderEvent.paymentFor.orderEventId)
                        .map(orderEventId => {
                            const orderEvent = orderEvents.find(
                                event => event._id === orderEventId,
                            );
                            if (!orderEvent)
                                throw new Error(
                                    `Order event ${orderEventId} not found`,
                                );
                            if (
                                !isOrderCreatedEvent(orderEvent) &&
                                !isOrderChangedEvent(orderEvent)
                            )
                                return undefined;
                            return orderEvent;
                        })
                        .filter(exists) as (
                        | OrderCreatedEvent
                        | OrderChangedEvent
                    )[];

                    return onPremise
                        ? _onPremiseFinancialOrderHistoryEventFactory({
                              status: "FULFILLED",
                              parents,
                              orderEvent,
                              orderId,
                              orderGroupId,
                          })
                        : waived
                          ? _waivedFinancialOrderHistoryEventFactory({
                                status: "FULFILLED",
                                parents,
                                orderEvent,
                                orderId,
                                orderGroupId,
                            })
                          : _paymentFinancialOrderHistoryEventFactory({
                                status: "FULFILLED",
                                parent: orderCreatedEvent,
                                orderEvent,
                                orderId,
                                orderGroupId,
                            });
                }

                // Default payment events
                case "PENDING_PAYMENT_CONFIRMATION":
                case "PAYMENT_AMOUNT_RELEASED":
                case "PAYMENT_FAILED": {
                    const orderCreatedEvent = orderEvents.find(
                        item => item.type === "CREATED",
                    ) as OrderCreatedEvent | undefined;
                    if (!orderCreatedEvent)
                        throw new Error("Order created event not found");
                    return _paymentFinancialOrderHistoryEventFactory({
                        status:
                            orderEvent.type === "PAYMENT_FAILED" ||
                            orderEvent.type === "PAYMENT_AMOUNT_RELEASED"
                                ? "CANCELLED"
                                : "PENDING",
                        parent: orderCreatedEvent,
                        orderEvent,
                        orderId,
                        orderGroupId,
                    });
                }

                // REFUNDS
                // - When a refund is requested
                // - we create a new refund receipt
                // - that will incorporate all refund transactions
                // - for that refund request
                case "FULL_REFUND_REQUESTED": {
                    // A full refund is requested
                    // everything in the order that has been paid
                    // will be refunded
                    const receipt: RefundReceiptForCustomer = {
                        _id: financialOrderInformationForCustomerEventIdFactory(),
                        _createdAt: orderEvent._createdAt,
                        _type: "FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT",
                        type: "REFUND",
                        status: "PENDING",
                        amount: orderEvent.amount,
                        reasonType: "FULL",
                        signature: receiptSignatureForRefundFactory(orderEvent),
                        originTypes: [],
                        refunds: [],
                        receiptFor: {
                            orderGroupId,
                            orderId,
                            providerId,
                            orderEventIds: [orderEvent._id],
                        },
                        items: [
                            payRequestItemFactory.from({
                                name:
                                    orderEvent.reason ??
                                    translation({
                                        sv: "Full återbetalning",
                                        en: "Full refund",
                                    }),
                                pricePerItem: orderEvent.amount,
                                quantity: 1,
                                providerId,
                                references: {
                                    orderId,
                                    orderGroupId,
                                    providerId,
                                    orderEventId: orderEvent._id,
                                },
                                type: "ADDED",
                                group: {
                                    key: orderEvent._id,
                                    eventAt: orderEvent._createdAt,
                                    title: translation({
                                        sv: "Avbokning",
                                        en: "Cancellation",
                                    }),
                                },
                            }),
                        ],
                    };
                    return receipt;
                }
                case "PARTIAL_REFUND_REQUESTED": {
                    // A partial refund is requested
                    // amount is a custom amount set by the provider
                    // we have not information about that anything in the
                    // order has changed
                    const receipt: RefundReceiptForCustomer = {
                        _id: financialOrderInformationForCustomerEventIdFactory(),
                        _createdAt: orderEvent._createdAt,
                        _type: "FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT",
                        type: "REFUND",
                        status: "PENDING",
                        amount: orderEvent.amount,
                        reasonType: "PARTIAL",
                        signature: receiptSignatureForRefundFactory(orderEvent),
                        originTypes: [],
                        refunds: [],
                        receiptFor: {
                            orderGroupId,
                            orderId,
                            providerId,
                            orderEventIds: [orderEvent._id],
                        },
                        items: [
                            payRequestItemFactory.from({
                                name:
                                    orderEvent.reason ??
                                    translation({
                                        sv: "Delvis återbetalning",
                                        en: "Partial refund",
                                    }),
                                pricePerItem: orderEvent.amount,
                                quantity: 1,
                                providerId,
                                references: {
                                    orderId,
                                    orderGroupId,
                                    providerId,
                                    orderEventId: orderEvent._id,
                                },
                                type: "ADDED",
                                group: {
                                    key: orderEvent._id,
                                    eventAt: orderEvent._createdAt,
                                    title: translation({
                                        sv: "Delvis återbetalning",
                                        en: "Partial refund",
                                    }),
                                },
                            }),
                        ],
                    };
                    return receipt;
                }
                case "ORDER_CHANGE_REFUND_REQUESTED": {
                    // An order changed is a result of an order change
                    // committed by the provider
                    const changeEvents = orderEvents.filter(event =>
                        orderEvent.changeEventIds.includes(event._id),
                    );

                    // const includeEvents = orderEvents.filter(
                    //     r =>
                    //         isOrderChangedEvent(r) &&
                    //         orderEvent.otherEvents.includes(r._id),
                    // ) as OrderChangedEvent[];

                    if (
                        !changeEvents.length ||
                        changeEvents[0].type !== "ORDER_CHANGED"
                    )
                        throw new Error(
                            `Change event required and not found; ${orderEvent.changeEventIds.join(
                                ",",
                            )}; ${orderEvent._id}`,
                        );
                    const eventItems = changeEvents.flatMap(event => {
                        if (!isOrderChangedEvent(event)) return [];

                        return event.changes.map(change =>
                            payRequestItemFactory.fromOrderChange(
                                change,
                                event._id,
                                {
                                    eventAt: event._createdAt,
                                    key: event._id,
                                    title:
                                        orderEvent.reason ??
                                        translation({
                                            sv: "KVITTERING",
                                            en: "RECEIPT",
                                        }),
                                },
                            ),
                        );
                    });

                    const receipt: RefundReceiptForCustomer = {
                        _id: financialOrderInformationForCustomerEventIdFactory(),
                        _createdAt: orderEvent._createdAt,
                        _type: "FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT",
                        type: "REFUND",
                        status: "PENDING",
                        amount: orderEvent.amount,
                        reasonType: "ORDER_CHANGE",
                        signature: receiptSignatureForRefundFactory(orderEvent),
                        originTypes: ["ORDER_CHANGED"],
                        refunds: [],
                        receiptFor: {
                            orderGroupId,
                            orderId,
                            providerId,
                            orderEventIds: [
                                orderEvent._id,
                                ...changeEvents.map(event => event._id),
                            ],
                        },
                        items: [
                            ...eventItems,
                            // ...changeEvent.changes.map(change =>
                            //     payRequestItemFactory.fromOrderChange(
                            //         change,
                            //         changeEvent._id,
                            //         {
                            //             eventAt: changeEvent._createdAt,
                            //             key: changeEvent._id,
                            //             title:
                            //                 orderEvent.reason ??
                            //                 translation({
                            //                     sv: "Återbetalning p.g.a. orderändring",
                            //                     en: "Refund due to order change",
                            //                 }),
                            //         },
                            //     ),
                            // ),
                            // ...extraEvents,
                        ],
                    };
                    return receipt;
                }
                case "PAYMENT_REFUND_INITIATED":
                case "PAYMENT_REFUND_PENDING": {
                    // Find the request receipt
                    // and add the refund transaction to it
                    const requestReceipt: RefundReceiptForCustomer | undefined =
                        state.receipts
                            .filter(isRefundReceiptForCustomer)
                            .find(receipt =>
                                receipt.receiptFor.orderEventIds.includes(
                                    orderEvent.paymentFor.orderEventId,
                                ),
                            );
                    if (!requestReceipt)
                        throw new Error(
                            `Request receipt not found; ${orderEvent.paymentFor.orderEventId}; ${orderEvent._id}`,
                        );

                    // Has it already been set to pending?
                    const exists = requestReceipt.refunds.find(
                        refund =>
                            refund.signature ===
                            `REFUND|${orderEvent.paymentProvider}`,
                    );
                    if (exists) return undefined;

                    // Push a pending refund status to refund array
                    return {
                        ...requestReceipt,
                        refunds: [
                            ...requestReceipt.refunds,
                            {
                                _type: "REFUND_TRANSACTION_STATUS",
                                _createdAt: orderEvent._createdAt,
                                status: "PENDING",
                                amount: orderEvent.requestedRefund,
                                paymentProvider: orderEvent.paymentProvider,
                                signature: `REFUND|${orderEvent.paymentProvider}`,
                            },
                        ],
                    };
                }
                case "PAYMENT_REFUND_FAILED": {
                    // Find the request receipt
                    // and add the refund transaction to it
                    const requestReceipt: RefundReceiptForCustomer | undefined =
                        state.receipts
                            .filter(isRefundReceiptForCustomer)
                            .find(receipt =>
                                receipt.receiptFor.orderEventIds.includes(
                                    orderEvent.paymentFor.orderEventId,
                                ),
                            );
                    if (!requestReceipt)
                        throw new Error(
                            `Request receipt not found; ${orderEvent.paymentFor.orderEventId}; ${orderEvent._id}`,
                        );

                    // Find the refund transaction
                    const currentIndex = requestReceipt.refunds.findIndex(
                        refund =>
                            refund.signature ===
                            `REFUND|${orderEvent.paymentProvider}`,
                    );
                    if (currentIndex < 0)
                        throw new Error(
                            `Refund transaction not found; ${orderEvent.paymentFor.orderEventId}; ${orderEvent._id}`,
                        );

                    // Push a pending refund status to refund array
                    return {
                        ...requestReceipt,
                        refunds: requestReceipt.refunds.map((refund, index) => {
                            if (index !== currentIndex) return refund;
                            const update: RefundTransactionStatus = {
                                ...refund,
                                _createdAt: orderEvent._createdAt,
                                status: "CANCELLED",
                            };
                            return update;
                        }),
                    };
                }
                case "PAYMENT_REFUND_FULFILLED": {
                    // Find the request receipt
                    // and add the refund transaction to it
                    const requestReceipt: RefundReceiptForCustomer | undefined =
                        state.receipts
                            .filter(isRefundReceiptForCustomer)
                            .find(receipt =>
                                receipt.receiptFor.orderEventIds.includes(
                                    orderEvent.paymentFor.orderEventId,
                                ),
                            );
                    if (!requestReceipt)
                        throw new Error(
                            `Request receipt not found; ${orderEvent.paymentFor.orderEventId}; ${orderEvent._id}`,
                        );

                    // Find the refund transaction
                    const currentIndex = requestReceipt.refunds.findIndex(
                        refund =>
                            refund.signature ===
                            `REFUND|${orderEvent.paymentProvider}`,
                    );
                    if (currentIndex < 0)
                        throw new Error(
                            `Refund transaction not found; ${orderEvent.paymentFor.orderEventId}; ${orderEvent._id}`,
                        );

                    // Push a pending refund status to refund array
                    const refundTransactions = requestReceipt.refunds.map(
                        (refund, index) => {
                            if (index !== currentIndex) return refund;
                            const update: RefundTransactionStatus = {
                                ...refund,
                                _createdAt: orderEvent._createdAt,
                                status: "FULFILLED",
                            };
                            return update;
                        },
                    );
                    return {
                        ...requestReceipt,
                        status:
                            refundTransactions.length &&
                            refundTransactions.every(
                                refund => refund.status === "FULFILLED",
                            )
                                ? "FULFILLED"
                                : "PENDING",
                        refunds: refundTransactions,
                    };
                }
                default:
                    return undefined;
            }
        };
        const receipt = getFinancialOrderInformationForCustomerEvent();
        const getUpdatedAmounts =
            (): FinancialOrderInformationForCustomerProjection["current"] => {
                const updatedAmounts = {
                    ...state.current,
                };
                switch (orderEvent.type) {
                    case "PAY_REQUEST_FULFILLED": {
                        // Lägg till beloppet på amount settled
                        const amountSettled = state.current.amountSettled || 0;
                        updatedAmounts.amountSettled =
                            amountSettled + orderEvent.price.amountIncludingVat;

                        // Plocka bort beloppet från amount unsettled
                        const amountUnsettled =
                            state.current.amountUnsettled || 0;
                        updatedAmounts.amountUnsettled =
                            amountUnsettled -
                            orderEvent.price.amountIncludingVat;
                        return updatedAmounts;
                    }
                    case "PAYMENT_AMOUNT_CAPTURABLE_UPDATED": {
                        // Öka amount capturable med beloppet från eventet
                        const amountCapturable =
                            state.current.amountCapturable || 0;
                        updatedAmounts.amountCapturable =
                            amountCapturable + orderEvent.amountCapturable;

                        // Dra bort beloppet från amount unsettled, vi har tillgång till pengarna nu
                        const amountUnsettled =
                            state.current.amountUnsettled || 0;
                        updatedAmounts.amountUnsettled =
                            amountUnsettled - orderEvent.amountCapturable;
                        return updatedAmounts;
                    }
                    case "PAYMENT_FULFILLED": {
                        // Betalningen är nu genomförd, lägg till beloppet på amount settled
                        const amountSettled = state.current.amountSettled || 0;
                        updatedAmounts.amountSettled =
                            amountSettled +
                            orderEvent.paidAmount.amountIncludingVat;

                        // Subtract charged amount on amount capturable on payment fulfilled if there
                        // has been an amount capturable event for the same order event
                        const hasHadAnAmountCapturableEvent = orderEvents.some(
                            event =>
                                event.type ===
                                    "PAYMENT_AMOUNT_CAPTURABLE_UPDATED" &&
                                event.paymentFor.orderEventId ===
                                    orderEvent.paymentFor.orderEventId,
                        );
                        if (hasHadAnAmountCapturableEvent) {
                            // The amount was previously capturable
                            const amountCapturable =
                                state.current.amountCapturable || 0;
                            updatedAmounts.amountCapturable =
                                amountCapturable -
                                orderEvent.paidAmount.amountIncludingVat;
                        } else {
                            // Else the amount must have been unsettled
                            const amountUnsettled =
                                state.current.amountUnsettled || 0;
                            updatedAmounts.amountUnsettled =
                                amountUnsettled -
                                orderEvent.paidAmount.amountIncludingVat;
                        }
                        return updatedAmounts;
                    }
                    case "PAYMENT_REFUND_FAILED": {
                        // Plocka bort beloppet från amount pending refund
                        const amountPendingRefund =
                            state.current.amountPendingRefund || 0;
                        updatedAmounts.amountPendingRefund =
                            amountPendingRefund -
                            orderEvent.requestedRefundAmount;
                        return updatedAmounts;
                    }
                    case "PAYMENT_REFUND_FULFILLED": {
                        // Lägg till beloppet på amount refunded
                        const amountRefunded =
                            state.current.amountRefunded || 0;
                        updatedAmounts.amountRefunded =
                            amountRefunded +
                            orderEvent.refundedAmount.amountIncludingVat;

                        // Plocka bort beloppet från amount pending refund
                        const amountPendingRefund =
                            state.current.amountPendingRefund || 0;
                        updatedAmounts.amountPendingRefund =
                            amountPendingRefund -
                            orderEvent.refundedAmount.amountIncludingVat;

                        // Plocka bort beloppet från amount settled
                        const amountSettled = state.current.amountSettled || 0;
                        updatedAmounts.amountSettled =
                            amountSettled +
                            orderEvent.refundedAmount.amountIncludingVat;

                        // Lägg till beloppet på amount unsettled
                        const amountUnsettled =
                            state.current.amountUnsettled || 0;
                        updatedAmounts.amountUnsettled =
                            amountUnsettled +
                            orderEvent.refundedAmount.amountIncludingVat;
                        return updatedAmounts;
                    }
                    case "PAYMENT_REFUND_INITIATED": {
                        const amountToRefund =
                            orderEvent.requestedRefund.amountIncludingVat;

                        // Lägg till beloppet på amount pending refund
                        const amountPendingRefund =
                            state.current.amountPendingRefund || 0;
                        updatedAmounts.amountPendingRefund =
                            amountPendingRefund + amountToRefund;

                        const amountSettled = state.current.amountSettled || 0;
                        updatedAmounts.amountSettled =
                            amountSettled - amountToRefund;

                        // Lägg till beloppet på amount unsettled
                        const amountUnsettled =
                            state.current.amountUnsettled || 0;
                        updatedAmounts.amountUnsettled =
                            amountUnsettled - amountToRefund;
                        return updatedAmounts;
                    }
                    case "PAY_REQUEST_CANCELLED":
                    case "PAY_REQUEST_PENDING":
                    case "PAY_REQUEST_EXPIRED":
                    case "PAYMENT_REFUND_PENDING":
                    case "PAYMENT_FAILED":
                    case "PENDING_PAYMENT_ONPREMISE":
                    case "ON_PREMISE_PAY_LATER_CANCELLED":
                    default:
                        return state.current;
                }
            };

        const amounts = getUpdatedAmounts();
        const prevReceiptExists = state.receipts.some(
            item => item.signature === receipt?.signature,
        );

        return {
            ...state,
            status: amounts.amountUnsettled === 0 ? "SETTLED" : "PENDING",
            current: amounts,
            // Check if receipts is an update to an existing receipt
            // if so replace it instead of adding a new one
            receipts: [
                ...state.receipts.map(item =>
                    item.signature === receipt?.signature ? receipt : item,
                ),
                ...(prevReceiptExists ? [] : receipt ? [receipt] : []),
            ],
        };
    };

    const financialOrderInformation = orderEvents
        // ignore the created event, it's part of the initial state already
        .filter(item => item.type !== "CREATED")
        // make sure events are sorted in the correct order
        .sort(sortBy(item => item._version))
        // reduce the events into a financial order information projection
        .reduce(financialOrderInformationForCustomerReducer, initial);

    return financialOrderInformation;
};

export {
    getFinancialOrderInformationForCustomer,
    receiptSignatureForRefundFactory,
};
